import { useTranslation } from 'react-i18next';
import type { AccountingHistoryResponse, AccountingValue } from '@pokerroom/types';
import { coreCategories, coreSubtotal, periodIncome } from './accountingReportModel';

/** Shared read-only figures for current-venue reports and the authorized cross-venue overview. */
export function AccountingOverviewSummary({
  data,
  onSelectDay,
  selectedDate,
}: {
  data: AccountingHistoryResponse;
  onSelectDay: (date: string) => void;
  selectedDate?: string;
}) {
  const { t } = useTranslation('accounting');
  const period = periodIncome(data.days);
  const cell = 'p-2 whitespace-nowrap tabular-nums';
  const amount = (value?: AccountingValue) => (
    <span>
      {value?.value ?? t('overview.unavailable')}
      {value?.reasonCode && (
        <small className="block text-base-content/60">
          {t(`dataReasons.${value.reasonCode}`, { defaultValue: t('overview.unavailable') })}
        </small>
      )}
      {value && value.status !== 'READY' && value.value !== null && (
        <small className="block text-warning">{t(`overview.${value.status}`)}</small>
      )}
    </span>
  );
  const dayButton = (date: string) => (
    <button
      type="button"
      aria-expanded={selectedDate === date}
      className="min-h-11 rounded border border-base-300 px-3 py-2"
      onClick={() => onSelectDay(date)}
    >
      {date}
    </button>
  );
  return (
    <>
      <div>
        <h2 className="font-semibold">{t('overview.incomeTable')}</h2>
        <p className="text-xs text-base-content/60">{t('overview.incomeNotice')}</p>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[880px] text-left text-sm">
          <caption className="sr-only">{t('overview.incomeTable')}</caption>
          <thead>
            <tr>
              <th className={cell}>{t('overview.date')}</th>
              <th className={cell}>{t('overview.status')}</th>
              {coreCategories.map((c) => (
                <th className={`${cell} text-right`} key={c}>
                  {t(`overview.${c}`)}
                </th>
              ))}
              <th className={`${cell} text-right`}>{t('overview.coreSubtotal')}</th>
              <th className={`${cell} text-right`}>{t('overview.BEVERAGE')}</th>
              <th className={`${cell} text-right`}>{t('overview.income')}</th>
            </tr>
          </thead>
          <tbody>
            {data.days.map((d) => (
              <tr key={d.businessDate} className="border-t border-base-300">
                <th className={cell}>{dayButton(d.businessDate)}</th>
                <td className={cell}>{t(`overview.${d.status}`)}</td>
                {d.status === 'NOT_YET' ? (
                  // A future day has no figures yet; dashes avoid reading it as zero.
                  <td className={`${cell} text-right text-base-content/40`} colSpan={8}>
                    —
                  </td>
                ) : (
                  <>
                    {coreCategories.map((c) => (
                      <td className={`${cell} text-right`} key={c}>
                        {amount(d.categories[c])}
                      </td>
                    ))}
                    <td className={`${cell} text-right font-semibold`}>
                      {amount(coreSubtotal(d))}
                    </td>
                    <td className={`${cell} text-right`}>{amount(d.categories.BEVERAGE)}</td>
                    <td className={`${cell} text-right font-semibold`}>{amount(d.income)}</td>
                  </>
                )}
              </tr>
            ))}
          </tbody>
          {data.days.length > 1 && (
            <tfoot>
              <tr className="border-t-2 border-base-300 font-semibold">
                <th className={cell} colSpan={2}>
                  {t('overview.periodTotal')}
                </th>
                {coreCategories.map((c) => (
                  <td className={`${cell} text-right`} key={c}>
                    {amount(period.categories[c])}
                  </td>
                ))}
                <td className={`${cell} text-right`}>{amount(period.core)}</td>
                <td className={`${cell} text-right`}>{amount(period.categories.BEVERAGE)}</td>
                <td className={`${cell} text-right`}>{amount(period.income)}</td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
      <div>
        <h2 className="font-semibold">{t('overview.balanceTable')}</h2>
        <p className="text-xs text-base-content/60">{t('overview.units')}</p>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[560px] text-left text-sm">
          <caption className="sr-only">{t('overview.balanceTable')}</caption>
          <thead>
            <tr>
              <th className={cell}>{t('overview.date')}</th>
              <th className={`${cell} text-right`}>{t('overview.drawer')} (TWD)</th>
              <th className={`${cell} text-right`}>{t('overview.reserve')} (TWD)</th>
              <th className={`${cell} text-right`}>{t('overview.petty')} (TWD)</th>
              <th className={`${cell} text-right`}>{t('overview.safeDeposit')} (TWD)</th>
            </tr>
          </thead>
          <tbody>
            {data.days.map((d) => (
              <tr key={d.businessDate} className="border-t border-base-300">
                <th className={cell}>{d.businessDate}</th>
                <td className={`${cell} text-right`}>{amount(d.drawer)}</td>
                <td className={`${cell} text-right`}>{amount(d.reserve)}</td>
                <td className={`${cell} text-right`}>{amount(d.petty)}</td>
                <td className={`${cell} text-right`}>{amount(d.safeDeposit)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}
